<script lang="ts" setup>
  import { useLocale } from '@tundr/i18n';
  import { computed, useSlots } from 'vue';
  import { visuallyHiddenClass } from '../../_shared/styles/visually-hidden.css';
  import { Tooltip } from '../../Tooltip';
  import {
    containerClass,
    disabledReasonTriggerClass,
    errorTextClass,
    extraLabelClass,
    helperTextClass,
    labelRowClass,
    labelRowSmallClass,
    labelTextClass,
    labelTextSmallClass,
    labelTrailingClass,
    messageSmallClass,
    notRequiredClass,
    warningTextClass,
  } from './FormField.css';
  import { FieldSize } from './FormField.types';
  import type { FormFieldProps } from './FormField.types';

  const props = withDefaults(defineProps<FormFieldProps>(), {
    optionalLabel: false,
    meta: undefined,
    size: FieldSize.MD,
    announceWarning: true,
  });

  const { t } = useLocale('ds-components');
  const slots = useSlots();

  const isSmall = computed<boolean>(() => props.size === FieldSize.SM);

  const isMetaInvalid = computed<boolean>(() =>
    props.meta ? !props.meta.valid && props.meta.touched : false,
  );

  const hasError = computed<boolean>(() => {
    if (!props.error) {
      return false;
    }
    return props.meta ? isMetaInvalid.value : true;
  });

  const errorId = computed(() => (props.id ? `${props.id}-error` : undefined));
  const labelId = computed(() =>
    props.id && props.label ? `${props.id}-label` : undefined,
  );
  const helperId = computed(() =>
    props.id ? `${props.id}-helper` : undefined,
  );

  const warningId = computed(() =>
    props.id ? `${props.id}-warning` : undefined,
  );

  const disabledReasonId = computed(() =>
    props.id && props.disabledReason
      ? `${props.id}-disabled-reason`
      : undefined,
  );

  const hasHelper = computed(() => !!props.helperText);
  const showWarning = computed(() => !hasError.value && !!props.warning);

  const describedBy = computed(() => {
    const ids = [
      hasError.value ? errorId.value : undefined,
      hasHelper.value ? helperId.value : undefined,
      showWarning.value ? warningId.value : undefined,
      disabledReasonId.value,
    ].filter(Boolean);
    return ids.length > 0 ? ids.join(' ') : undefined;
  });
</script>

<template>
  <div :class="containerClass">
    <div
      v-if="label || slots.extraLabel || slots.labelTrailing"
      :class="[labelRowClass, isSmall && labelRowSmallClass]"
    >
      <label
        v-if="label"
        :id="labelId"
        :for="id"
        :class="[labelTextClass, isSmall && labelTextSmallClass]"
        >{{
          label
        }}</label
      >
      <span v-if="optionalLabel" :class="notRequiredClass"
        >({{ t('form_field.not_required') }})</span
      >
      <span v-if="slots.extraLabel" :class="extraLabelClass">
        <slot name="extraLabel" />
      </span>
      <span v-if="slots.labelTrailing" :class="labelTrailingClass">
        <slot name="labelTrailing" />
      </span>
    </div>

    <Tooltip v-if="disabledReason" :description="disabledReason">
      <div :class="disabledReasonTriggerClass">
        <slot
          :label-id="labelId"
          :error-id="errorId"
          :helper-id="helperId"
          :described-by="describedBy"
          :has-error="hasError"
        />
      </div>
    </Tooltip>
    <slot
      v-else
      :label-id="labelId"
      :error-id="errorId"
      :helper-id="helperId"
      :described-by="describedBy"
      :has-error="hasError"
    />

    <span
      v-if="disabledReason"
      :id="disabledReasonId"
      :class="visuallyHiddenClass"
    >
      {{ disabledReason }}
    </span>

    <div
      v-if="hasHelper"
      :id="helperId"
      :class="
        hasError
          ? visuallyHiddenClass
          : [helperTextClass, isSmall && messageSmallClass]
      "
    >
      {{ helperText }}
    </div>

    <div
      v-if="hasError"
      :id="errorId"
      :class="[errorTextClass, isSmall && messageSmallClass]"
      role="alert"
    >
      {{ error }}
    </div>

    <div
      v-if="announceWarning || showWarning"
      :id="warningId"
      :class="
        showWarning ? [warningTextClass, isSmall && messageSmallClass] : undefined
      "
      :role="announceWarning ? 'status' : undefined"
      :aria-live="announceWarning ? 'polite' : undefined"
    >
      <template v-if="showWarning">{{ warning }}</template>
    </div>
  </div>
</template>
